import { cn } from "@/lib/utils";
import { forwardRef } from "react";
import type { LucideIcon } from "lucide-react";

export interface ChipProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  active?: boolean;
  icon?: LucideIcon;
  badge?: number | string;
  badgeTone?: "brand" | "amber" | "rose" | "blue" | "slate";
}

const badgeToneMap = {
  brand: "bg-brand-600 text-white",
  amber: "bg-amber-500 text-white",
  rose:  "bg-rose-600  text-white",
  blue:  "bg-blue-600  text-white",
  slate: "bg-slate-500 text-white",
};

export const Chip = forwardRef<HTMLButtonElement, ChipProps>(function Chip(
  { active, icon: Icon, badge, badgeTone = "brand", className, children, ...rest }, ref
) {
  return (
    <button
      ref={ref}
      type="button"
      aria-pressed={active}
      className={cn(
        "inline-flex items-center gap-2 rounded-full border px-3.5 py-2 text-sm font-medium transition focus-ring whitespace-nowrap",
        active
          ? "bg-brand-600 text-white border-brand-600 shadow-xs"
          : "bg-card text-muted-foreground border-border hover:bg-muted/40 hover:text-foreground",
        className
      )}
      {...rest}
    >
      {Icon && <Icon className={cn("h-4 w-4", active ? "text-white" : "text-muted-foreground")} />}
      <span>{children}</span>
      {badge !== undefined && badge !== null && badge !== 0 && (
        <span className={cn(
          "ml-0.5 inline-flex min-w-[1.25rem] items-center justify-center rounded-full px-1.5 text-[11px] font-semibold",
          active ? "bg-white/20 text-white" : badgeToneMap[badgeTone]
        )}>
          {badge}
        </span>
      )}
    </button>
  );
});
